import {
  EntityLinks,
  Goal,
  GoalGroupBy,
  GoalLinkRow,
  GoalProgressCount,
  GoalSortingMode,
  GroupByMode,
  ID,
  LifeArea,
  lifeAreaOtherID,
} from '@griply/types';

import {
  buildGoalsById,
  getGoalLifeAreas,
  getGoalTopLevelLifeAreas,
} from '../get-goal-life-area';
import { sortGoals } from '../sort-goals';
import { sortItems } from '../sort-items';
import {
  compareDeadlineQuarterKeys,
  getDeadlineQuarterKey,
} from './deadline-quarters';
import { getPresentParentIds, isAlsoRoot } from './get-present-parent-ids';

export enum GoalCategoryKind {
  LifeArea = 'life-area',
  Quarter = 'quarter',
  Ungrouped = 'ungrouped',
}

export type GoalCategoryNode = {
  id: ID;
  goal: Goal;
  subCategories: GoalCategoryNode[];
};

export type GoalCategory = {
  id: ID;
  kind: GoalCategoryKind;
  lifeArea?: LifeArea;
  quarterKey?: string;
  subCategories: GoalCategoryNode[];
};

export type BuildGoalCategoryTreeOptions = {
  goals: Goal[];
  // Unfiltered set: a sub-goal keeps its inherited life area when a filter drops its parent.
  allGoals: Goal[];
  lifeAreas: LifeArea[];
  links: EntityLinks;
  groupBy?: GoalGroupBy;
  // Canonical order of every goal at every depth, as `useGoalsOverviewOrder` builds it.
  customOrder?: ID[] | null;
  // Per-parent child order; a goal under several parents holds a position under each.
  goalChildrenByParentId?: Map<ID, ID[]>;
  // Open tasks per goal; the Tasks sort mode no-ops without it.
  taskCounts?: Map<ID, number>;
  goalProgressCounts?: Map<ID, GoalProgressCount>;
  // The goal or life area the timeline is rooted in; see `isAlsoRoot`.
  rootLink?: GoalLinkRow;
};

export const buildGoalCategoryTree = ({
  goals,
  allGoals,
  lifeAreas,
  links,
  groupBy,
  customOrder,
  goalChildrenByParentId,
  taskCounts,
  goalProgressCounts,
  rootLink,
}: BuildGoalCategoryTreeOptions): GoalCategory[] => {
  const goalsById = buildGoalsById(allGoals);
  const lifeAreaCache = new Map<ID, ID[]>();
  const getLifeAreaIds = (goal: Goal) =>
    getGoalLifeAreas(goal, { goalsById, links, cache: lifeAreaCache });
  const nodesById = new Map<ID, GoalCategoryNode>();

  goals.forEach((goal) => {
    nodesById.set(goal.id, { id: goal.id, goal, subCategories: [] });
  });

  const getPresentParents = (goal: Goal) =>
    getPresentParentIds(goal, (id) => nodesById.has(id), {
      links,
      goalsById,
    });

  // The node object is shared deliberately so a subtree established later is visible from all parents.
  goals.forEach((goal) => {
    const node = nodesById.get(goal.id);
    if (!node) {
      return;
    }
    getPresentParents(goal).forEach((parentId) => {
      nodesById.get(parentId)?.subCategories.push(node);
    });
  });

  const sortChildren = (
    subCategories: GoalCategoryNode[],
    parentGoal: Goal,
  ): GoalCategoryNode[] => {
    const parentOrder = goalChildrenByParentId?.get(parentGoal.id);
    const childGoals = subCategories.map(({ goal }) => goal);
    const sortedChildren = parentOrder
      ? sortItems(childGoals, parentOrder, 'id')
      : sortGoals(childGoals, {
          allGoals: goals,
          sortMode: customOrder
            ? GoalSortingMode.Custom
            : parentGoal.goalSortingMode,
          customOrder: customOrder ?? parentGoal.goalSorting,
          taskCounts,
          goalProgressCounts,
          links,
        });

    return toNodes(sortedChildren);
  };

  // Goal ids on the current path — a corrupt parent cycle would otherwise recurse forever.
  const pathIds = new Set<ID>();

  const toNodes = (categoryGoals: Goal[]): GoalCategoryNode[] =>
    categoryGoals
      .map(({ id }) => nodesById.get(id))
      .filter(
        (node): node is GoalCategoryNode => !!node && !pathIds.has(node.id),
      )
      .map((node) => {
        pathIds.add(node.id);
        const subCategories = sortChildren(node.subCategories, node.goal);
        pathIds.delete(node.id);

        return { ...node, subCategories };
      });

  const orderRoots = (roots: Goal[], lifeAreaSorting?: ID[] | null) =>
    sortGoals(roots, {
      allGoals: goals,
      sortMode: GoalSortingMode.Custom,
      customOrder: customOrder ?? lifeAreaSorting,
      links,
    });

  // A link to the list's root (or, unrooted, an absent parent) also makes a nested goal a root.
  const rootGoals = goals.filter(
    (goal) =>
      !getPresentParents(goal).length ||
      isAlsoRoot(goal, (id) => nodesById.has(id), {
        links,
        goalsById,
        rootLink,
      }),
  );

  // deadline
  if (groupBy === GroupByMode.Deadline) {
    const goalsByQuarter = new Map<string, Goal[]>();
    orderRoots(rootGoals).forEach((goal) => {
      const key = getDeadlineQuarterKey(goal.deadline);
      goalsByQuarter.set(key, [...(goalsByQuarter.get(key) ?? []), goal]);
    });

    return [...goalsByQuarter.keys()]
      .sort(compareDeadlineQuarterKeys)
      .map((quarterKey) => ({
        id: quarterKey,
        kind: GoalCategoryKind.Quarter,
        quarterKey,
        subCategories: toNodes(goalsByQuarter.get(quarterKey) ?? []),
      }));
  }

  // no grouping
  if (groupBy !== GroupByMode.LifeArea) {
    return [
      {
        id: lifeAreaOtherID,
        kind: GoalCategoryKind.Ungrouped,
        subCategories: toNodes(orderRoots(rootGoals)),
      },
    ];
  }

  const knownLifeAreaIds = new Set(lifeAreas.map(({ id }) => id));
  const topLevelLifeAreaIds = new Map(
    goals.map((goal) => [
      goal.id,
      getGoalTopLevelLifeAreas(goal, {
        isNested: !!getPresentParents(goal).length,
        goalsById,
        links,
        cache: lifeAreaCache,
      }),
    ]),
  );

  // A goal in several life areas is listed under each of them.
  const categories: GoalCategory[] = lifeAreas.map((lifeArea) => {
    const lifeAreaGoals = goals.filter((goal) =>
      topLevelLifeAreaIds.get(goal.id)?.includes(lifeArea.id),
    );

    return {
      id: lifeArea.id,
      kind: GoalCategoryKind.LifeArea,
      lifeArea,
      subCategories: lifeAreaGoals.length
        ? toNodes(orderRoots(lifeAreaGoals, lifeArea.goalSorting))
        : [],
    };
  });

  // A goal whose life areas were all archived or filtered away still needs a home.
  const orphanRoots = rootGoals.filter(
    (goal) => !getLifeAreaIds(goal).some((id) => knownLifeAreaIds.has(id)),
  );

  if (orphanRoots.length) {
    categories.push({
      id: lifeAreaOtherID,
      kind: GoalCategoryKind.LifeArea,
      subCategories: toNodes(orderRoots(orphanRoots)),
    });
  }

  return categories;
};
